import { CompanyFlags, CompanyIntegrations } from '@global-interfaces/Company';
import { ErpStatus, FlowStatus, ProcoreStatus } from '@global-interfaces/enums';
import Invoice from '@global-interfaces/Invoice';
import LineItem from '@global-interfaces/LineItem';
import { ValueLabelSelector } from '@global-interfaces/ValueLabelSelector';
import {
  STATUS_NEW,
  PROCORE,
  QUICKBOOKS,
  QUICKBOOKS_DESKTOP,
  SAGE,
  STATUS_NONE,
  STATUS_SYNCED,
  STATUS_PROCESSING,
  STATUS_FAILED,
  STATUS_READY,
  QUICKBOOKS_DESKTOP_MAC,
  CANCELLED,
  DISPUTED
} from '@global-utils/defaultValues';
import { PAYMENT_TYPE_CHECK } from '@global-utils/paymentDefaultValues';
import { addDays, isWeekend } from 'date-fns';
import {
  getMemorialDay,
  getIndependenceDay,
  getLaborDay,
  getThanksgiving,
  getNewYearsDay,
  getMartinLutherKingJrDay,
  getPresidentsDay,
  getVeteransDay,
  getChristmas,
  getJuneteenth,
  getColumbusDay
} from 'date-fns-holiday-us';
import { RawDraftContentState } from 'draft-js';

export const formatDate = (dateValue, UTC = true, time = false) => {
  if (dateValue === null || dateValue === undefined) {
    return null;
  }

  if ((typeof dateValue === 'string' || dateValue instanceof String) && !dateValue.length) {
    return;
  }

  let date = dateValue;

  if (!(dateValue instanceof Date)) {
    date = new Date(dateValue);
  }

  return new Intl.DateTimeFormat('en-US', {
    ...(UTC ? { timeZone: 'UTC' } : undefined),
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    ...(time ? { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false } : undefined)
  }).format(date);
};

export const formatTimestamp = (dateValue: number, UTC = true, time = false) => {
  if (dateValue === null || dateValue === undefined) {
    return null;
  }

  const date = new Date(dateValue);

  return new Intl.DateTimeFormat('en-US', {
    ...(UTC ? { timeZone: 'UTC' } : undefined),
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    ...(time ? { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false } : undefined)
  }).format(date);
};

export const formatTimestampLong = (timestamp: string) => {
  if (!timestamp) {
    return '';
  }
  const date = new Date(parseInt(timestamp));
  const dateString = new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).format(
    date
  );

  const timeString = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }).format(
    date
  );

  const timezoneString = date.toLocaleTimeString('en-us', { timeZoneName: 'short' }).split(' ')[2];
  return `${dateString} at ${timeString} ${timezoneString}`;
};

export const formatToSend = (dateValue) => {
  if (dateValue instanceof Date) {
    return `${dateValue.getFullYear()}-${dateValue.getMonth() + 1}-${dateValue.getDate()}`;
  }

  return null;
};

export const formatCurrency = (value: number) => {
  const numericValue = typeof value === 'number' ? value : 0;

  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const formatted = formatter.format(numericValue);
  const negativeNumber = formatted.startsWith('-');
  const amount = negativeNumber ? formatted.slice(1) : formatted;
  const symbol = negativeNumber ? '-$' : '$';

  return `${symbol} ${amount}`;
};

// biome-ignore lint/suspicious/noExplicitAny: safe to ignore
export const isNumeric = (value: any) => {
  return !isNaN(parseInt(value));
};

export const unformatDate = (dateString?: string | null) => {
  if (dateString === null || dateString === undefined || !dateString.length) {
    return null;
  }

  //MM/DD/YY to YYYY-MM-DD
  const array = dateString.split('/');
  return `20${array[2]}-${array[0]}-${array[1]}`;
};

/**
 * Add N U.S. banking–business days to a date.
 * • "Business day" = Mon‑Fri and not a federal‑reserve holiday (real or observed)
 * • If the timestamp is **after 13:00 PT** the start day does NOT count.
 */
export const addBusinessDays = (date: Date, days: number): Date => {
  const afterCutoff = date.getHours() >= 13; // 1 p.m. PT
  let result = new Date(date);
  let count = 0;

  // --- Observed holiday helpers ---
  const observedHolidaySetForYear = (year: number): Date[] => {
    const base = [
      getNewYearsDay(year),
      getMartinLutherKingJrDay(year),
      getPresidentsDay(year),
      getMemorialDay(year),
      getJuneteenth(year),
      getIndependenceDay(year),
      getLaborDay(year),
      getColumbusDay(year),
      getVeteransDay(year),
      getThanksgiving(year),
      getChristmas(year)
    ];
    const all: Date[] = [];
    for (const h of base) {
      const dow = h.getDay();
      if (dow === 6) {
        // Saturday: observed Friday only
        all.push(addDays(h, -1));
      } else if (dow === 0) {
        // Sunday: observed Monday only
        all.push(addDays(h, 1));
      } else {
        // Weekday: real date only
        all.push(h);
      }
    }
    return all;
  };

  // Build holiday set for prev/current/next year
  const holidays: Date[] = [
    ...observedHolidaySetForYear(date.getFullYear() - 1),
    ...observedHolidaySetForYear(date.getFullYear()),
    ...observedHolidaySetForYear(date.getFullYear() + 1)
  ];

  const isHoliday = (d: Date) => holidays.some((h) => h.toDateString() === d.toDateString());
  const isBusinessDay = (d: Date) => !isWeekend(d) && !isHoliday(d);

  // --- Inclusive/exclusive cutoff logic ---
  if (!afterCutoff && isBusinessDay(result)) {
    count = 1;
  } else {
    // Move to the next business day if after cutoff or not a business day
    do {
      result = addDays(result, 1);
    } while (!isBusinessDay(result));
    count = 1;
  }

  // Walk forward until we've tallied the requested number of business days
  while (count < days) {
    result = addDays(result, 1);
    if (isBusinessDay(result)) {
      count++;
    }
  }

  return result;
};

const isStartOfDay = (date: Date): boolean =>
  date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0;

const getPacificDateTimeParts = (date: Date): { year: number; month: number; day: number; hour: number } => {
  const parts = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    hour: 'numeric',
    hour12: false,
    hourCycle: 'h23',
    month: 'numeric',
    timeZone: 'America/Los_Angeles',
    year: 'numeric'
  }).formatToParts(date);

  const valueByType: Record<string, string> = {};
  for (const { type, value } of parts) {
    valueByType[type] = value;
  }

  return {
    day: Number(valueByType.day),
    hour: Number(valueByType.hour),
    month: Number(valueByType.month),
    year: Number(valueByType.year)
  };
};

export const isAfterPaymentCutoff = (date: Date): boolean => {
  if (isStartOfDay(date)) {
    return false;
  }

  return getPacificDateTimeParts(date).hour >= 13;
};

const getPaymentCalculationDate = (date: Date): Date => {
  const calculationDate = new Date(date);

  if (!isStartOfDay(date)) {
    const { year, month, day } = getPacificDateTimeParts(date);
    calculationDate.setFullYear(year, month - 1, day);
  }

  calculationDate.setHours(isAfterPaymentCutoff(date) ? 13 : 0, 0, 0, 0);
  return calculationDate;
};

export const getPaymentArrivalDate = (date: Date, paymentType: string): Date => {
  const businessDays = paymentType === PAYMENT_TYPE_CHECK ? 9 : 3;
  return addBusinessDays(getPaymentCalculationDate(date), businessDays);
};

type ErpCollection =
  | typeof SAGE
  | typeof QUICKBOOKS
  | typeof QUICKBOOKS_DESKTOP
  | typeof PROCORE
  | typeof QUICKBOOKS_DESKTOP_MAC;

interface erpStatusResponse {
  text: null | string;
  className: null | string;
}

interface InvoiceErpStatusCollection {
  erp_status: ErpStatus;
  ei_dynamics_response?: string | null;
  quickbooks_response?: string | null;
  quickbooks_status: ErpStatus;
  quickbooks_desktop_response?: string | null;
  quickbooks_desktop_status: ErpStatus;
  procore_response?: string | null;
  procore_status: ProcoreStatus;
  procore_reference_url?: string;
}

export const erpStatusCollection = (
  invoice: InvoiceErpStatusCollection,
  company: { integrations_available: CompanyIntegrations },
  alignLeft = false
) => {
  const {
    erp_status,
    ei_dynamics_response,
    quickbooks_response,
    quickbooks_status,
    quickbooks_desktop_response,
    quickbooks_desktop_status
  } = invoice;

  const {
    sage_available,
    quickbooks_available,
    quickbooks_desktop_available,
    quickbooks_desktop_mac_available
  } = company.integrations_available || {};

  // biome-ignore format:  no break line
  const classNameReady = alignLeft ? 'text-capitalize ribbon-two ribbon-two-info' : 'text-capitalize ribbon-three table-td ribbon-three-info';
  // biome-ignore format:  no break line
  const classNameProcessing = alignLeft ? 'text-capitalize ribbon-two ribbon-two-info' : 'text-capitalize ribbon-three table-td ribbon-three-warning';
  // biome-ignore format:  no break line
  const classNameDone = alignLeft ? 'text-capitalize ribbon-two ribbon-two-success' : 'text-capitalize ribbon-three table-td ribbon-three-success';
  // biome-ignore format:  no break line
  const classNameFailed = alignLeft ? 'text-capitalize ribbon-two ribbon-two-danger' : 'text-capitalize ribbon-three table-td ribbon-three-danger';

  const getErpStatus: {
    (
      erpResponse: string,
      erp: ErpCollection,
      sep: '' | '-'
    ): {
      [STATUS_NONE]: erpStatusResponse;
      [ProcoreStatus.NEW]: erpStatusResponse;
      [ProcoreStatus.READY]: erpStatusResponse;
      [ProcoreStatus.PROCESSING]: erpStatusResponse;
      [ProcoreStatus.DONE]: erpStatusResponse;
      [ProcoreStatus.FAILURE]: erpStatusResponse;
      [ErpStatus.NEED]: erpStatusResponse;
      [ErpStatus.VOIDED]: erpStatusResponse;
    };
  } = (erpResponse, erp, sep) => {
    const processingText = erp === PROCORE ? `${erpResponse} &#x1F7E1;` : `Syncing with ${erp} &#x1F7E1;`;
    return {
      [STATUS_NONE]: { text: null, className: null },
      [ProcoreStatus.NEW]: { text: null, className: null },
      [ErpStatus.NEED]: { text: null, className: null },
      [ErpStatus.VOIDED]: { text: null, className: null },
      [ProcoreStatus.READY]: { text: `Ready to Sync with ${erp} &#x1F7E1;`, className: classNameReady },
      [ProcoreStatus.PROCESSING]: { text: processingText, className: classNameProcessing },
      [ProcoreStatus.DONE]: {
        text: `Synced with ${erp} &#128994;`,
        className: classNameDone
      },
      // biome-ignore format:  no break line
      [ProcoreStatus.FAILURE]: { text: `Failed to Sync with ${erp}: &#128308; </br> ${sep} ${erpResponse}`, className: classNameFailed }
    };
  };

  //QUICKBOOKS_DESKTOP_MAC has the same status as QBDesktop
  const erps = [
    { erp: SAGE, available: sage_available, status: erp_status },
    { erp: QUICKBOOKS, available: quickbooks_available, status: quickbooks_status },
    { erp: QUICKBOOKS_DESKTOP, available: quickbooks_desktop_available, status: quickbooks_desktop_status },
    { erp: QUICKBOOKS_DESKTOP_MAC, available: quickbooks_desktop_mac_available, status: quickbooks_desktop_status },
  ];

  const checkRibbonEnabled = () => {
    return erps.some((erp) => erp.available && erp.status !== STATUS_NEW);
  };

  const enabled = checkRibbonEnabled();

  // biome-ignore format:  no break line
  const { text: text_erp, className: className_erp } = getErpStatus(ei_dynamics_response ?? '', SAGE, '')[sage_available ? erp_status : STATUS_NONE];

  // biome-ignore format:  no break line
  const { text: text_qb, className: className_qb } = getErpStatus(quickbooks_response ?? '', QUICKBOOKS, '-')[quickbooks_available ? quickbooks_status : STATUS_NONE];

  // biome-ignore format:  no break line
  const { text: text_qbd, className: className_qbd } = getErpStatus(quickbooks_desktop_response ?? '', QUICKBOOKS_DESKTOP, '-')[quickbooks_desktop_available ? quickbooks_desktop_status : STATUS_NONE];

  // biome-ignore format:  no break line
  const { text: text_qb_mac, className: className_qb_mac } = getErpStatus(quickbooks_desktop_response ?? '', QUICKBOOKS_DESKTOP_MAC, '-')[quickbooks_desktop_mac_available ? quickbooks_desktop_status : STATUS_NONE];

  const texts = [text_erp, text_qb, text_qbd, text_qb_mac];
  const classNames = [className_erp, className_qb, className_qbd, className_qb_mac];

  const buildText = () => {
    const availableTexts = texts.filter((t) => Boolean(t));

    return availableTexts.join('</br></br>');
  };

  //check if order is relevant here
  const className = classNames.find((cn) => cn) || '';

  const availableErps = erps
    .filter((erp) => erp.available)
    .filter((erp) => ![STATUS_NEW, STATUS_NONE].includes(erp.status));
  const availableErp = availableErps?.[0] || STATUS_NONE;

  const _MAP_RIBBON_STATUS = {
    done: STATUS_SYNCED,
    processing: STATUS_PROCESSING,
    failed: STATUS_FAILED,
    ready: STATUS_READY,
    new: '',
    none: ''
  };

  const label = _MAP_RIBBON_STATUS[availableErp.status];

  return {
    enabled,
    ribbon: {
      className,
      label
    },
    toolTip: buildText()
  };
};

export const checkArrayEquality = (a, b) => {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((value, index) => value === b[index]);
};

export const browserTls13 = () => {
  if (navigator.userAgent.indexOf('Firefox') != -1) {
    if (parseInt(navigator.userAgent.match(/Firefox\/(.*)/)![1]) >= 63) {
      return true;
    } else {
      return false;
    }
  } else if (navigator.userAgent.indexOf('Edg') != -1) {
    if (parseInt(navigator.userAgent.match(/Edg\/(.*)/)![1]) >= 79) {
      return true;
    } else {
      return false;
    }
  } else if (navigator.userAgent.indexOf('Chrome') != -1) {
    if (parseInt(navigator.userAgent.match(/Chrom(e|ium)\/(.*)/)![2]) >= 69) {
      return true;
    } else {
      return false;
    }
  } else if (navigator.userAgent.indexOf('Edge') != -1) {
    if (parseInt(navigator.userAgent.match(/Edge\/(.*)/)![1]) >= 79) {
      return true;
    } else {
      return false;
    }
  } else if (navigator.userAgent.indexOf('Safari') != -1) {
    return true;
  } else {
    return false;
  }
};

export const getBrowser = () => {
  if (navigator.userAgent.indexOf('Firefox') != -1) {
    return 'Firefox';
  } else if (navigator.userAgent.indexOf('Edg') != -1) {
    return 'Edge';
  } else if (navigator.userAgent.indexOf('Chrome') != -1) {
    return 'Chrome';
  } else if (navigator.userAgent.indexOf('Edge') != -1) {
    return 'Edge';
  } else if (navigator.userAgent.indexOf('Safari') != -1) {
    return 'Safari';
  } else {
    return 'Unknown';
  }
};

export const checkNewMentionsIds = (notes, mentioned) => {
  const newMentions = mentioned.filter((mention) => notes.includes(mention.display));
  return newMentions.map((user) => Number(user.id));
};

export const getMentionedUsers = (content: RawDraftContentState) => {
  const mentions = Object.values(content.entityMap)
    .filter((entity) => entity.type === 'MENTION')
    .map((mention) => mention.data.url);
  return mentions;
};

export const formatEin = (ein) => {
  if (!ein) {
    return null;
  }
  return ein.substring(0, 2).concat('-', ein.substring(2, ein.length));
};

export const returnHasEPayment = (vendor: {
  has_electronic_payment?: boolean;
  bank?: {
    account_number?: string | null;
    routing_number?: string | null;
    masked_account_number?: string | null;
    masked_routing_number?: string | null;
    balance_id?: number | null;
    mercoa_id?: string | number | null;
  } | null;
}) => {
  if (vendor.has_electronic_payment === true) {
    return true;
  }
  const bank = vendor?.bank;
  return bank
    ? Boolean(
        (bank.masked_account_number && bank.masked_routing_number) ||
          (bank.account_number && bank.routing_number) ||
          bank.balance_id ||
          bank.mercoa_id
      )
    : false;
};

export const getAlertMessage = (
  status: 'valid' | 'invalid' | 'expired' | 'error' | 'password-error' | 'password-invalid'
) => {
  switch (status) {
    case 'valid':
      return 'Verification successful. Closing...';
    case 'invalid':
      return 'The code you entered is invalid. Please try again.';
    case 'expired':
      return 'The code you entered has expired. Please try again.';
    case 'password-error':
      return 'Password is required to disable 2-factor authentication.';
    case 'password-invalid':
      return 'The password you entered is incorrect. Please try again.';
    default:
      return 'An error occurred. Please try again or contact support.';
  }
};

export const fileRejectionErrorMessage = (fileRejections, accept) => {
  if (fileRejections.length === 0) {
    return null;
  }

  if (fileRejections.length > 0 && fileRejections[0].errors[0].code === 'file-invalid-type') {
    const normalizedAccept = Object.keys(accept).map((key) => {
      const fileTypes = (accept as { [key: string]: string[] })[key];

      if (fileTypes?.length > 0) {
        return fileTypes.map((item) => `*${item}`).join(', ');
      }

      if (key !== 'image/*' && key.includes('image/')) {
        return `*.${key.replace('image/', '')}`;
      }

      switch (key) {
        case 'image/*':
          return 'image';

        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          return '*.xlsx';

        case 'application/vnd.ms-excel':
          return '*.xls';

        case 'application/msword':
          return '*.doc';

        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          return '*.docx';

        case 'application/vnd.ms-powerpoint':
          return '*.ppt';

        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          return '*.pptx';

        case 'application/pdf':
          return '*.pdf';

        case 'text/plain':
          return '*.txt';

        default:
          return '';
      }
    });

    return `Only ${normalizedAccept.join(', ')} files are accepted`;
  }

  if (fileRejections.length > 0 && fileRejections[0].errors[0].code === 'file-too-large') {
    return 'File size is too big';
  }

  if (fileRejections.length > 0 && fileRejections[0].errors[0].code === 'too-many-files') {
    return 'Only one file is accepted';
  }

  return null;
};

export const round = (value: number) => {
  return Number((Math.round(value * 1000) / 1000).toFixed(2));
};

export const getOptionLabel = (option: string | ValueLabelSelector, options: ValueLabelSelector[]) => {
  if (typeof option === 'string') {
    return option;
  } else {
    const item = options.find((o) => o.label === option.label);

    if (item) {
      return item.label;
    } else {
      return '';
    }
  }
};

const getSyncedErps = (invoice: Invoice, companyFlags: CompanyFlags) => {
  const erps = [
    {
      erp: 'Procore',
      isSynced: invoice?.procore_status === ProcoreStatus.DONE,
      flag: companyFlags?.lock_invoice_after_procore_sync
    },
    {
      erp: 'Quickbooks Desktop',
      isSynced: invoice?.quickbooks_desktop_status === ErpStatus.DONE,
      flag: companyFlags?.lock_invoice_after_QBD_sync
    },
    {
      erp: 'Quickbooks Online',
      isSynced: invoice?.quickbooks_status === ErpStatus.DONE,
      flag: companyFlags?.lock_invoice_after_QBO_sync
    }
  ];

  return erps.filter((erp) => erp.isSynced && erp.flag);
};

export const getInvoiceLockedMessage = (invoice: Invoice, companyFlags: CompanyFlags) => {
  if (
    !companyFlags?.lock_invoice_after_procore_sync &&
    !companyFlags?.lock_invoice_after_QBD_sync &&
    !companyFlags?.lock_invoice_after_QBO_sync &&
    !companyFlags?.lock_invoice_when_bp_is_closed
  ) {
    return false;
  }

  if ([CANCELLED, DISPUTED].includes(invoice.flow_status)) {
    return 'The lines items cannot be changed because the invoice is in a status that does not allow it.';
  }

  if (![FlowStatus.APPROVED, FlowStatus.PAID].includes(invoice.flow_status)) {
    return false;
  }

  let msg = '';

  if (companyFlags?.lock_invoice_when_bp_is_closed && invoice.billing_period?.is_closed) {
    msg = `This invoice is assigned to a closed billing period.`;
  }

  const syncedErps = getSyncedErps(invoice, companyFlags);

  if (!syncedErps.length) {
    return msg ?? false;
  }

  msg = msg
    ? `${msg} It's also synced with ${syncedErps.map((erp) => erp.erp).join(', ')}`
    : `This invoice is synced with ${syncedErps.map((erp) => erp.erp).join(', ')}`;

  return msg;
};

/**
 * Performs precise decimal calculations for financial operations by converting to cents
 * to avoid JavaScript floating-point arithmetic issues.
 * @param amount1 First number to multiply
 * @param amount2 Second number to multiply
 * @returns The precise product of the two numbers, rounded to 2 decimal places
 */
export const preciseMultiply = (amount1: number, amount2: number): number => {
  // Convert first amount to cents to avoid floating point issues
  const amount1Cents = Math.round(amount1 * 100);
  // Multiply in cents then convert back to dollars
  return Math.round(amount1Cents * amount2) / 100;
};

export const preciseDivide = (amount1: number, amount2: number): number => {
  // Return 0 if denominator is 0 to avoid infinity
  if (amount2 === 0) {
    return 0;
  }

  // Convert first amount to cents to avoid floating point issues
  const amount1Cents = Math.round(amount1 * 100);
  // Divide in cents then convert back to dollars
  return Math.round(amount1Cents / amount2) / 100;
};

export const getNextOrderNumber = (string: string) => {
  if (!string) {
    return '';
  }

  const reversedString = string.split('').reverse().join('');

  const match = reversedString.match(/\d+/);
  const numbersArray = match ? match[0].split('').reverse() : [];
  if (!numbersArray.length) {
    return `${string}-2`;
  }
  const orderNumber = numbersArray.join('');
  const nextOrderNumber = Number(orderNumber) + 1;
  const paddedNextOrderNumber = nextOrderNumber.toString().padStart(orderNumber.length, '0');
  const startIndex = string.lastIndexOf(orderNumber);
  const endIndex = startIndex + orderNumber.length;
  return string.slice(0, startIndex) + paddedNextOrderNumber + string.slice(endIndex);
};

export const getQuantityAndUnitCost = (lineItem: LineItem) => {
  if (
    lineItem.period_values &&
    lineItem.period_values !== 0 &&
    (lineItem.quantity === 0 || !lineItem.quantity) &&
    (lineItem.unit_cost === 0 || !lineItem.unit_cost)
  ) {
    return { quantity: 1, unit_cost: lineItem.period_values };
  } else if (
    lineItem.period_values &&
    lineItem.period_values !== 0 &&
    lineItem.quantity &&
    lineItem.quantity !== 0 &&
    lineItem.unit_cost === 0
  ) {
    return {
      quantity: lineItem.quantity,
      unit_cost: preciseDivide(lineItem.period_values, lineItem.quantity || 1)
    };
  }

  return { quantity: 0, unit_cost: lineItem.period_values };
};

export const getNetAmount = (lineItem: LineItem) => {
  if (Number(lineItem.net_amount) === 0) {
    return lineItem.amount - lineItem.retainage_amount + lineItem.tax_amount;
  }
  return lineItem.net_amount;
};
